// Define union types for different question answer types
export type RadioAnswer = string;
export type CheckboxAnswer = string[];
export type SliderAnswer = number;
export type TextAnswer = string;

// Union type for all possible answer values
export interface SelectAnswer {
  type: 'select';
  value: string;
  otherValue?: string;
}

export interface SuccessorFormAnswer {
  firstName?: string;
  lastName?: string;
  relationship?: string;
  relationshipOther?: string;
}

export interface DateSliderAnswer {
  type: 'date' | 'slider';
  value: Date | number;
}

export interface MultiFieldTextAnswer {
  [fieldId: string]: string;
}

export interface DynamicRepeatingAnswer {
  entries: Array<Record<string, string | number | boolean>>;
}

export interface FileUploadAnswer {
  files: {
    fileName: string;
    fileSize: number;
    fileType: string;
    documentType?: string;
    uploadDate: string;
    isValid: boolean;
  }[];
}

export interface AddressAnswer {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  country?: string;
  postalCode?: string;
}

export type QuestionAnswer = RadioAnswer | CheckboxAnswer | SliderAnswer | TextAnswer | SelectAnswer | SuccessorFormAnswer | DateSliderAnswer | MultiFieldTextAnswer | AddressAnswer | DynamicRepeatingAnswer | FileUploadAnswer;

// Type-safe answer data record
export type AnswerData = Record<string, QuestionAnswer>;

// Helper type guards for answer validation
export function isRadioAnswer(value: unknown): value is RadioAnswer {
  return typeof value === 'string';
}

export function isCheckboxAnswer(value: unknown): value is CheckboxAnswer {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
}

export function isSliderAnswer(value: unknown): value is SliderAnswer {
  return typeof value === 'number';
}

export function isTextAnswer(value: unknown): value is TextAnswer {
  return typeof value === 'string';
}

export function isSelectAnswer(value: unknown): value is SelectAnswer {
  return value !== null && typeof value === 'object' && 'type' in value && value.type === 'select';
}

export function isSuccessorFormAnswer(value: unknown): value is SuccessorFormAnswer {
  return value !== null && typeof value === 'object' && 'firstName' in value;
}

export function isDateSliderAnswer(value: unknown): value is DateSliderAnswer {
  return value !== null && typeof value === 'object' && 
    'type' in value && typeof value.type === 'string' && 
    ['date', 'slider'].includes(value.type);
}

export function isMultiFieldTextAnswer(value: unknown): value is MultiFieldTextAnswer {
  return value && typeof value === 'object' && !Array.isArray(value);
}

export function isAddressAnswer(value: unknown): value is AddressAnswer {
  return value && typeof value === 'object' && !Array.isArray(value);
}

export function isDynamicRepeatingAnswer(value: unknown): value is DynamicRepeatingAnswer {
  return value !== null && typeof value === 'object' && 'entries' in value && Array.isArray(value.entries);
}

export function isFileUploadAnswer(value: unknown): value is FileUploadAnswer {
  return value !== null && typeof value === 'object' && 'files' in value && Array.isArray(value.files);
}